import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMaintenanceCategories, useCreateMaintenanceRequest } from '@/hooks/useMaintenance'
import api from '@/lib/api'

const schema = z.object({
  title: z.string().min(1),
  category: z.string().min(1),
  category_note: z.string().optional(),
  description: z.string().min(1),
})

type FormValues = z.infer<typeof schema>

export default function NewMaintenancePage() {
  const { t } = useTranslation('maintenance')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { data: categories, isLoading: categoriesLoading } = useMaintenanceCategories()
  const createMutation = useCreateMaintenanceRequest()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  })

  const watchedCategory = watch('category')
  const [uploadError, setUploadError] = useState(false)

  // LIFF-H2: this used to call the OPERATOR /api/presigned_urls (403 for
  // customer tokens) and destructure a nonexistent public_url — so no LIFF
  // customer ever successfully attached a photo, silently.
  const uploadPhoto = async (file: File): Promise<string> => {
    // Customer-scoped presigned URL; response is { url, key }
    const presignedRes = await api.post<{
      url: string
      key: string
    }>('/api/customer/presigned_urls', { content_type: file.type })
    const { url, key } = presignedRes.data

    // Upload to Tigris/S3 (plain fetch — no app auth headers on a presigned PUT)
    const putRes = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    })
    if (!putRes.ok) throw new Error(`upload failed: ${putRes.status}`)

    // Store the BARE key (storage convention) — clients render /api/images/<key>
    return key
  }

  const onSubmit = async (values: FormValues) => {
    const imageUrls: string[] = []

    const file = fileInputRef.current?.files?.[0]
    if (file) {
      try {
        const key = await uploadPhoto(file)
        imageUrls.push(key)
      } catch {
        // LIFF-H2: surface the failure — do NOT silently submit photoless
        setUploadError(true)
        return
      }
    }
    setUploadError(false)

    await createMutation.mutateAsync({
      ...values,
      // Only send category_note when category is 'other'
      category_note: values.category === 'other' ? values.category_note : undefined,
      image_urls: imageUrls,
    })

    navigate('/maintenance')
  }

  return (
    <div className="p-4 pb-8">
      <form onSubmit={(e) => { void handleSubmit(onSubmit)(e) }} className="space-y-4">
        {/* Title */}
        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">{t('title_label')}</span>
          </label>
          <input
            type="text"
            className={`input input-bordered w-full ${errors.title ? 'input-error' : ''}`}
            placeholder={t('title_placeholder')}
            {...register('title')}
          />
          {errors.title && (
            <label className="label">
              <span className="label-text-alt text-error">{tc('errors.required')}</span>
            </label>
          )}
        </div>

        {/* Category */}
        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">{t('category_label')}</span>
          </label>
          {categoriesLoading ? (
            <span className="loading loading-spinner loading-sm" />
          ) : (
            <select
              className={`select select-bordered w-full ${errors.category ? 'select-error' : ''}`}
              {...register('category')}
              defaultValue=""
            >
              <option value="" disabled>{t('category_placeholder')}</option>
              {categories?.map((cat) => (
                <option key={cat.key} value={cat.key}>
                  {cat.th} / {cat.en}
                </option>
              ))}
            </select>
          )}
          {errors.category && (
            <label className="label">
              <span className="label-text-alt text-error">{tc('errors.required')}</span>
            </label>
          )}
        </div>

        {/* Category Note (shown only when category is 'other') */}
        {watchedCategory === 'other' && (
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">{t('category_note')}</span>
            </label>
            <input
              type="text"
              className="input input-bordered w-full"
              placeholder={t('category_note_placeholder')}
              {...register('category_note')}
            />
          </div>
        )}

        {/* Description */}
        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">{t('description_label')}</span>
          </label>
          <textarea
            className={`textarea textarea-bordered w-full min-h-24 ${errors.description ? 'textarea-error' : ''}`}
            placeholder={t('description_placeholder')}
            {...register('description')}
          />
          {errors.description && (
            <label className="label">
              <span className="label-text-alt text-error">{tc('errors.required')}</span>
            </label>
          )}
        </div>

        {/* Photo */}
        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">{t('photo_label')}</span>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="file-input file-input-bordered w-full"
          />
        </div>

        {uploadError && (
          <div className="alert alert-error text-sm">
            <span>{t('photo_upload_failed')}</span>
          </div>
        )}

        {createMutation.error && (
          <div className="alert alert-error text-sm">
            <span>{tc('errors.something_went_wrong')}</span>
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary w-full"
          disabled={isSubmitting || createMutation.isPending}
        >
          {(isSubmitting || createMutation.isPending) ? (
            <span className="loading loading-spinner loading-sm" />
          ) : (
            tc('actions.submit')
          )}
        </button>
      </form>
    </div>
  )
}
